/**
 * The context bar's axes and their cascade — the PURE part, with no Vue and no store.
 *
 * Separated from `use-context-selection.ts` for a very concrete reason: that composable
 * imports `useHashParams` from `@fondationcss/ui`, and the published package loads Vuetify's
 * CSS per component, which Vitest cannot transform. All the cascade logic would therefore be
 * untestable if it lived next to it. Here, it is tested without mounting anything.
 */

import { translate } from '@fondationcss/sdk';
import { isGroupTrainingEnabled } from '@/utils/group-training-feature';

export type ContextAxis = 'site' | 'program' | 'group' | 'teacher';

/** The bar's cascade order: choosing an axis clears everything downstream of it. */
export const AXIS_ORDER: readonly ContextAxis[] = ['site', 'program', 'group', 'teacher'];

export const HASH_PARAM: Record<ContextAxis, string> = {
  site: 'gsite',
  program: 'gprogram',
  group: 'ggroup',
  teacher: 'gteacher',
};

/**
 * The fragment parameters to write for a selection: the axis's own, then every downstream one
 * reset to `undefined`.
 *
 * That is the half of the work that is easily forgotten. `context-toolbar.vue`'s handlers wrote
 * the store AND those parameters; a landing page that had only done the first half would have
 * produced a selection lost on reload and a cascade that never empties.
 */
export function cascadeHashParams(
  axis: ContextAxis,
  id: string | undefined
): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = { [HASH_PARAM[axis]]: id };
  for (const downstream of AXIS_ORDER.slice(AXIS_ORDER.indexOf(axis) + 1)) {
    params[HASH_PARAM[downstream]] = undefined;
  }
  return params;
}

/** The minimum a program must expose to be filed in the menu. */
export interface MenuProgram {
  id: string;
  isIndividual?: boolean | null;
}

/**
 * The two groups of the Programme menu, once the kind the route imposes has been applied.
 *
 * Three routes only admit one kind — assiduity a group program, assignments and the
 * consolidated view an individual one. They nevertheless offered both, and acceptance testing
 * raised it twice: one could choose « Soudage-montage » on the assignments page only to land on
 * a guard. A menu that offers what the screen refuses is a dead end.
 *
 * The program ALREADY selected stays listed even if it is of the wrong kind, and that is
 * deliberate: the context bar is persisted, so one often arrives here with the wrong selection
 * coming from another screen. Clearing it silently would lose a choice made elsewhere, and
 * above all the bar would display « Programme » while the page explains that a group program is
 * chosen — two contradictory statements. By keeping it visible, the bar says what is chosen,
 * the page says why it does not fit, and the menu only offers valid replacements.
 */
export function programMenuGroups<T extends MenuProgram>(
  programs: readonly T[],
  requiredType: 'individual' | 'group' | undefined,
  selectedId: string | null | undefined
): { group: T[]; individual: T[] } {
  const group = programs.filter(p => !p.isIndividual);
  const individual = programs.filter(p => !!p.isIndividual);
  if (!requiredType) return { group, individual };

  // On the excluded side, we only keep the current selection — never a new choice.
  const keepSelectedOnly = (side: T[]) =>
    side.filter(p => !!selectedId && p.id === selectedId);

  return requiredType === 'individual'
    ? { group: keepSelectedOnly(group), individual }
    : { group, individual: keepSelectedOnly(individual) };
}

/**
 * The « Programme » menu, laid out (#948).
 *
 * The three family entries ARE the section headings: « Groupes » leads the group programs,
 * « Individualisés » leads the individualized ones, and « Tous les programmes » sits alone above
 * both. Before this, the headings were `<v-list-subheader>`s — not focusable, not clickable —
 * with a separate « Tous les individualisés » line under one of them saying the same thing
 * twice, and no equivalent at all on the group side.
 *
 * A section keeps a text `label` only when its family entry is absent, which happens for a
 * teacher whose scope forbids the unions (see the store's `offerProgramScopes`): the heading
 * still has to say what the list under it is.
 *
 * `scopeOf` maps a program to its family entry kind, or null for a real program — passed in so
 * this module stays free of the scope ids.
 */
export function programMenuSections<T extends MenuProgram>(
  programs: readonly T[],
  requiredType: 'individual' | 'group' | undefined,
  selectedId: string | null | undefined,
  scopeOf: (program: T) => 'individual' | 'group' | 'all' | null,
): { label?: string; entries: T[] }[] {
  const scopes = new Map<'individual' | 'group' | 'all', T>();
  const real: T[] = [];
  for (const program of programs) {
    const kind = scopeOf(program);
    if (kind) scopes.set(kind, program);
    else real.push(program);
  }

  const menu = programMenuGroups(real, requiredType, selectedId);
  // A route that admits one kind only must not offer the other family, nor « tous ».
  const offer = (kind: 'individual' | 'group' | 'all') =>
    (requiredType === undefined || requiredType === kind ? scopes.get(kind) : undefined);

  const sections: { label?: string; entries: T[] }[] = [];
  const all = offer('all');
  if (all) sections.push({ entries: [all] });

  const side = (kind: 'individual' | 'group', labelKey: string, list: T[]) => {
    const lead = offer(kind);
    if (!lead && !list.length) return;
    if (lead) sections.push({ entries: [lead, ...list] });
    else sections.push({ label: translate(labelKey), entries: list });
  };
  side('group', 'parcours.teaching.contextMenu.groupsLabel', menu.group);
  side('individual', 'parcours.teaching.contextMenu.individualLabel', menu.individual);
  return sections;
}

/**
 * The axes the bar draws on a route, out of those the route declares — read by the bar itself
 * (context-toolbar.vue) and by the header's mobile filter button (page-header.vue), which must
 * never offer an empty panel.
 *
 * - With « Formation par groupes » off (#812), the Groupe axis withdraws from every route.
 * - A supervisor-only account (#1164) gets none of the teacher's axes: sites, programs, groups,
 *   teachers and the presence type catalogue all answer it 403, and its only teacher screen,
 *   Présences in supervisor mode, reads the whole day's sittings whatever a menu would say.
 * - The specialist's « Groupe » (#1445) stays whatever else the account holds: its list is their
 *   own route's, which a specialist who also supervises exams reads like any other. It answers
 *   to the specialist's view, not to the teacher's flags.
 *
 * `flags` is `useSdk().Features.flags.value`, so the reading stays reactive.
 */
export function visibleContextAxes<T extends string>(
  declared: readonly T[],
  flags: Record<string, boolean>,
  examSupervisorOnly: boolean,
): T[] {
  const groupTraining = isGroupTrainingEnabled(flags);
  return declared.filter(axis => {
    if (axis === 'specialistGroup') return true;
    if (examSupervisorOnly) return false;
    return groupTraining || axis !== 'group';
  });
}
